import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ChevronLeft, Calendar, Clock, User, CheckCircle, AlertCircle, ExternalLink, Info, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getCourseById, assignInstructorToEnrollment } from "@/utils/firestoreService";
import { Enrollment } from "@/types/courseTypes";
import { useFirebase } from "@/contexts/FirebaseContext";
import { doc, getDoc, getFirestore, updateDoc } from "firebase/firestore";
import { firebaseConfig } from "@/config/firebase";
import { initializeApp } from "firebase/app";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// Initialize Firebase
let app;
try {
  app = initializeApp(firebaseConfig);
} catch (error) {
  console.error("Firebase initialization error:", error);
}

const db = getFirestore(app);

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  availability?: string[] | string;
  specialization?: string;
  rating?: number;
}

interface ExtendedEnrollment extends Enrollment {
  userId?: string;
  studentName?: string;
  studentEmail?: string;
  studentPhone?: string;
  studentAddress?: string;
  studentPostcode?: string;
  studentGearbox?: string;
  instructorName?: string;
  courseName?: string;
  // Public booking specific fields
  email?: string;
  phone?: string;
  pickupAddress?: string;
  postcode?: string;
  carType?: string;
  vehicleType?: string;
  preferredTime?: string;
  area?: string;
  // Additional fields that might be present
  licenseType?: string;
  specialRequirements?: string;
  preferredLanguage?: string;
  drivingExperience?: string;
  medicalConditions?: string;
  emergencyContact?: string;
  preferredLessonTime?: string;
  preferredLessonDays?: string;
  previousInstructor?: string;
  testDate?: string;
}

const InstructorAssignment = () => {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getAllInstructors } = useFirebase();
  const [enrollment, setEnrollment] = useState<ExtendedEnrollment | null>(null);
  const [course, setCourse] = useState<any | null>(null);
  const [student, setStudent] = useState<any | null>(null);
  const [availableInstructors, setAvailableInstructors] = useState<Instructor[]>([]);
  const [selectedInstructorId, setSelectedInstructorId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [isChangingInstructor, setIsChangingInstructor] = useState<boolean>(false);
  const [isUpdatingPostcode, setIsUpdatingPostcode] = useState(false);
  const [newPostcode, setNewPostcode] = useState("");

  useEffect(() => {
    const loadData = async () => {
      if (!enrollmentId) return;
      
      setLoading(true);
      try {
        // Fetch instructors
        const instructorsData = await getAllInstructors();
        const formattedInstructors = instructorsData.map((instructor: any) => ({
          id: instructor.id,
          firstName: instructor.firstName || "Unknown",
          lastName: instructor.lastName || "Instructor",
          email: instructor.email || "",
          role: instructor.role || "instructor",
          availability: Array.isArray(instructor.availability) 
            ? instructor.availability 
            : instructor.availability 
              ? [instructor.availability] 
              : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          specialization: instructor.specialization || "General driving instruction",
          rating: instructor.rating || 4.5
        }));
        setAvailableInstructors(formattedInstructors);
        
        // Fetch enrollment directly from Firestore
        console.log("Fetching enrollment from Firestore:", enrollmentId);
        const enrollmentRef = doc(db, "enrollments", enrollmentId);
        const enrollmentSnap = await getDoc(enrollmentRef);
        
        if (enrollmentSnap.exists()) {
          const enrollmentData = { 
            id: enrollmentSnap.id, 
            ...enrollmentSnap.data() 
          } as ExtendedEnrollment;
          
          console.log("Fetched enrollment:", enrollmentData);
          setEnrollment(enrollmentData);
          
          // Fetch course data
          if (enrollmentData.courseId) {
            const courseData = await getCourseById(enrollmentData.courseId);
            if (courseData) {
              setCourse(courseData);
            }
          }
          
          // Fetch student data from users collection
          if (enrollmentData.userId) {
            const studentRef = doc(db, "users", enrollmentData.userId);
            const studentSnap = await getDoc(studentRef);
            if (studentSnap.exists()) {
              const studentData = studentSnap.data();
              setStudent({
                id: enrollmentData.userId,
                firstName: studentData.firstName || enrollmentData.studentName?.split(' ')[0] || "Unknown",
                lastName: studentData.lastName || enrollmentData.studentName?.split(' ')[1] || "Student",
                email: studentData.email || enrollmentData.studentEmail || "No email provided",
                phone: studentData.phone || enrollmentData.studentPhone || "+44 7123 456789",
                address: studentData.address || enrollmentData.studentAddress || enrollmentData.pickupAddress || "Not provided",
                postcode: studentData.postcode || enrollmentData.studentPostcode || enrollmentData.postcode || "Not provided",
                carType: studentData.carType || enrollmentData.vehicleType || enrollmentData.carType || "Not specified",
                licenseType: studentData.licenseType || "Not specified",
                gearbox: studentData.gearbox || enrollmentData.studentGearbox || enrollmentData.carType || "Not specified",
                specialRequirements: studentData.specialRequirements || "None",
                preferredLanguage: studentData.preferredLanguage || "English",
                drivingExperience: studentData.drivingExperience || "None",
                medicalConditions: studentData.medicalConditions || "None",
                emergencyContact: studentData.emergencyContact || "Not provided",
                preferredLessonTime: studentData.preferredLessonTime || enrollmentData.preferredTime || "Not specified",
                preferredLessonDays: studentData.preferredLessonDays || "Not specified",
                previousInstructor: studentData.previousInstructor || "None",
                testDate: studentData.testDate || "Not scheduled"
              });
            } else {
              // Fallback to enrollment data if student document not found (for public bookings)
              setStudent({
                id: enrollmentData.userId,
                firstName: enrollmentData.studentName?.split(' ')[0] || "Unknown",
                lastName: enrollmentData.studentName?.split(' ')[1] || "Student",
                email: enrollmentData.studentEmail || enrollmentData.email || "No email provided",
                phone: enrollmentData.studentPhone || enrollmentData.phone || "+44 7123 456789",
                address: enrollmentData.studentAddress || enrollmentData.pickupAddress || "Not provided",
                postcode: enrollmentData.studentPostcode || enrollmentData.postcode || "Not provided",
                carType: enrollmentData.vehicleType || enrollmentData.carType || "Not specified",
                licenseType: enrollmentData.licenseType || "Not specified",
                gearbox: enrollmentData.studentGearbox || enrollmentData.carType || "Not specified",
                specialRequirements: enrollmentData.specialRequirements || "None",
                preferredLanguage: enrollmentData.preferredLanguage || "English",
                drivingExperience: enrollmentData.drivingExperience || "None",
                medicalConditions: enrollmentData.medicalConditions || "None",
                emergencyContact: enrollmentData.emergencyContact || "Not provided",
                preferredLessonTime: enrollmentData.preferredTime || "Not specified",
                preferredLessonDays: enrollmentData.preferredLessonDays || "Not specified",
                previousInstructor: enrollmentData.previousInstructor || "None",
                testDate: enrollmentData.testDate || "Not scheduled"
              });
            }
          }
          
          // If instructor is already assigned, set the selected instructor
          if (enrollmentData.instructorId) {
            setSelectedInstructorId(enrollmentData.instructorId);
          }
        } else {
          console.error("Enrollment not found");
          toast({
            title: "Error",
            description: "Enrollment not found",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast({
          title: "Error",
          description: "Failed to load assignment details",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [enrollmentId, getAllInstructors, toast]);

  const handleAssignInstructor = async () => {
    if (!selectedInstructorId || !enrollmentId) {
      toast({
        title: "Error",
        description: "Please select an instructor",
        variant: "destructive",
      });
      return;
    }

    // Check if student has a postcode
    if (!student?.postcode || student.postcode === "Not provided") {
      toast({
        title: "Error",
        description: "Cannot assign instructor: Student's postcode is missing. Please ask the student to update their profile with their postcode.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    
    try {
      const selectedInstructor = availableInstructors.find(i => i.id === selectedInstructorId);
      if (!selectedInstructor) {
        throw new Error("Selected instructor not found");
      }
      
      const instructorName = `${selectedInstructor.firstName} ${selectedInstructor.lastName}`;
      
      // Update the enrollment in Firestore with instructor email
      await assignInstructorToEnrollment(
        enrollmentId,
        selectedInstructorId,
        instructorName,
        selectedInstructor.email
      );
      
      // Update local state
      if (enrollment) {
        const updatedEnrollment = {
          ...enrollment,
          instructorId: selectedInstructorId,
          instructorName: instructorName,
          instructorEmail: selectedInstructor.email,
          status: "assigned" as const,
          updatedAt: new Date().toISOString()
        };
        setEnrollment(updatedEnrollment);
      }
      
      toast({
        title: "Success",
        description: "Instructor assigned successfully",
      });
      
      // Navigate back to bookings after a short delay
      setTimeout(() => {
        navigate("/admin-dashboard/bookings");
      }, 1500);
    } catch (error) {
      console.error("Error assigning instructor:", error);
      toast({
        title: "Error",
        description: "Failed to assign instructor",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeInstructor = () => {
    setIsChangingInstructor(true);
    setSelectedInstructorId("");
  };

  const handleUpdatePostcode = async () => {
    if (!newPostcode.trim()) {
      toast({
        title: "Error",
        description: "Please enter a valid postcode",
        variant: "destructive",
      });
      return;
    }

    try {
      const db = getFirestore();
      if (!student?.id) return;

      await updateDoc(doc(db, "users", student.id), {
        postcode: newPostcode.trim(),
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setStudent(prev => prev ? { ...prev, postcode: newPostcode.trim() } : null);
      setIsUpdatingPostcode(false);
      setNewPostcode("");

      toast({
        title: "Success",
        description: "Student's postcode has been updated",
      });
    } catch (error) {
      console.error("Error updating postcode:", error);
      toast({
        title: "Error",
        description: "Failed to update postcode",
        variant: "destructive",
      });
    }
  };

  const handleUpdateStudentInfo = () => {
    if (student?.id) {
      navigate(`/admin-dashboard/update-student-data?studentId=${student.id}`);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="admin">
        <div className="flex items-center justify-center h-64">
          <p>Loading enrollment details...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!enrollment || !course || !student) {
    return (
      <DashboardLayout role="admin">
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-lg">Enrollment information not found</p>
          <Button className="mt-4" onClick={() => navigate("/admin-dashboard/bookings")}>
            Back to Bookings
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="admin">
      <div className="mb-6">
        <Button 
          variant="ghost" 
          className="mb-4 hover:bg-gray-100 transition-colors"
          onClick={() => navigate("/admin-dashboard/bookings")}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to Bookings
        </Button>
        
        <h1 className="text-3xl font-bold text-gray-900 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Assign Instructor</h1>
        <p className="text-gray-600 mt-2">
          Match the best instructor for this {enrollment.courseType === "practical" ? "practical driving lesson" : 
                                          enrollment.courseType === "theory" ? "theory course" : 
                                          enrollment.courseType === "individual" ? "individual driving lesson" : 
                                          "course"}
        </p>
      </div>

      {(!student?.postcode || student.postcode === "Not provided") && (
        <Alert variant="destructive" className="mb-6 shadow-lg border-red-200">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Missing Student Information</AlertTitle>
          <AlertDescription>
            Cannot assign an instructor: Student's postcode is missing. Please ask the student to update their profile with their postcode before proceeding.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
              <CardTitle className="text-xl font-semibold">Booking Details</CardTitle>
              <CardDescription className="text-gray-600">
                Review booking information before assigning an instructor
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h3 className="font-medium mb-3 text-gray-800">Course Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Course:</span>
                      <span className="font-medium text-gray-800">{course.title}</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Type:</span>
                      <span className="text-gray-800">{enrollment.courseType === "practical" ? "Practical Driving" : 
                             enrollment.courseType === "theory" ? "Theory Course" : 
                             enrollment.courseType === "individual" ? "Individual Course" : 
                             enrollment.courseType}</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Duration:</span>
                      <span className="text-gray-800">{course.duration} hours</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Status:</span>
                      <span className={`font-medium ${enrollment.instructorId ? "text-green-600" : "text-amber-600"}`}>
                        {enrollment.instructorId ? "Instructor Assigned" : "Awaiting Assignment"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h3 className="font-medium mb-3 text-gray-800">Student Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Name:</span>
                      <span className="font-medium text-gray-800">{student?.firstName} {student?.lastName}</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Email:</span>
                      <span className="text-gray-800">{student?.email}</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Phone:</span>
                      <span className="text-gray-800">{student?.phone || "Not provided"}</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Address:</span>
                      <span className="text-gray-800">{student?.address || "Not provided"}</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Postcode:</span>
                      <div className="flex items-center gap-2">
                        <span className={`${!student?.postcode || student.postcode === "Not provided" ? "text-red-600 font-medium" : "text-gray-800"}`}>
                          {student?.postcode || "Not provided"}
                        </span>
                        {(!student?.postcode || student.postcode === "Not provided") && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleUpdateStudentInfo}
                            className="h-8 hover:bg-red-50 hover:text-red-600 transition-colors"
                          >
                            Update Info
                          </Button>
                        )}
                        {(!student?.postcode || student.postcode === "Not provided") && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="h-4 w-4 text-red-600" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Postcode is required to assign an instructor</p>
                                <p className="text-xs mt-1">It helps instructors plan their routes and schedule</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Gearbox:</span>
                      <span className="text-gray-800">{student?.gearbox || "Not specified"}</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">License Type:</span>
                      <span className="text-gray-800">{student?.licenseType || "Not specified"}</span>
                    </div>
                  </div>
                  {(!student?.postcode || student.postcode === "Not provided") && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md shadow-sm">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-800">Postcode Required</p>
                          <p className="text-sm text-red-700 mt-1">
                            Please update the student's postcode to proceed with instructor assignment.
                            <Button
                              variant="link"
                              className="h-auto p-0 ml-1 text-red-700 hover:text-red-800 transition-colors"
                              onClick={handleUpdateStudentInfo}
                            >
                              Update Postcode <ExternalLink className="h-3 w-3 ml-1" />
                            </Button>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="border-t pt-6">
                <h3 className="font-medium mb-3 text-gray-800">Additional Information</h3>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Special Requirements:</span>
                      <span className="text-gray-800">{student?.specialRequirements || "None"}</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Medical Conditions:</span>
                      <span className="text-gray-800">{student?.medicalConditions || "None"}</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Emergency Contact:</span>
                      <span className="text-gray-800">{student?.emergencyContact || "Not provided"}</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Preferred Language:</span>
                      <span className="text-gray-800">{student?.preferredLanguage || "English"}</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Lesson Time:</span>
                      <span className="text-gray-800">{student?.preferredLessonTime || "Not specified"}</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Lesson Days:</span>
                      <span className="text-gray-800">{student?.preferredLessonDays || "Not specified"}</span>
                    </div>
                    <div className="flex items-center py-1.5">
                      <span className="w-32 text-gray-500">Previous Instructor:</span>
                      <span className="text-gray-800">{student?.previousInstructor || "None"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {enrollment.instructorId && !isChangingInstructor ? (
            <Card className="mt-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="bg-gradient-to-r from-green-50 to-white border-b">
                <CardTitle className="flex items-center text-green-600">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Instructor Already Assigned
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center mr-4 shadow-sm">
                    <User className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-800">
                      {availableInstructors.find(i => i.id === enrollment?.instructorId) ? 
                        `${availableInstructors.find(i => i.id === enrollment?.instructorId)?.firstName} ${availableInstructors.find(i => i.id === enrollment?.instructorId)?.lastName}` : 
                        "Unknown Instructor"}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {availableInstructors.find(i => i.id === enrollment?.instructorId)?.email || "No email available"}
                    </p>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="bg-gray-50">
                <Button 
                  variant="outline" 
                  className="w-full hover:bg-white transition-colors"
                  onClick={handleChangeInstructor}
                >
                  Change Instructor
                </Button>
              </CardFooter>
            </Card>
          ) : (
            <Card className="mt-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-white border-b">
                <CardTitle className="text-xl font-semibold">{isChangingInstructor ? "Change Instructor" : "Assign Instructor"}</CardTitle>
                <CardDescription className="text-gray-600">
                  {isChangingInstructor ? 
                    `Select a new instructor for this ${enrollment.courseType === "practical" ? "practical driving lesson" : 
                                                     enrollment.courseType === "theory" ? "theory course" : 
                                                     enrollment.courseType === "individual" ? "individual driving lesson" : 
                                                     "course"}` : 
                    `Select an instructor for this ${enrollment.courseType === "practical" ? "practical driving lesson" : 
                                                  enrollment.courseType === "theory" ? "theory course" : 
                                                  enrollment.courseType === "individual" ? "individual driving lesson" : 
                                                  "course"}`}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="instructor" className="text-gray-700">Select Instructor</Label>
                    <Select 
                      value={selectedInstructorId} 
                      onValueChange={setSelectedInstructorId}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select an instructor" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableInstructors.map((instructor) => (
                          <SelectItem key={instructor.id} value={instructor.id}>
                            {instructor.firstName} {instructor.lastName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {selectedInstructorId && (
                    <div className="border rounded-md p-4 mt-4 bg-white shadow-sm">
                      <div className="flex items-center">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center mr-4 shadow-sm">
                          <User className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">
                            {availableInstructors.find(i => i.id === selectedInstructorId)?.firstName} {availableInstructors.find(i => i.id === selectedInstructorId)?.lastName}
                          </h3>
                          <div className="flex items-center text-sm text-gray-500">
                            <span className="flex items-center">
                              Specialization: {availableInstructors.find(i => i.id === selectedInstructorId)?.specialization}
                            </span>
                            <span className="mx-2">•</span>
                            <span>
                              Rating: {availableInstructors.find(i => i.id === selectedInstructorId)?.rating}/5
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4">
                        <h4 className="text-sm font-medium mb-2 text-gray-700">Availability</h4>
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            const instructor = availableInstructors.find(i => i.id === selectedInstructorId);
                            const availability = instructor?.availability;
                            
                            if (Array.isArray(availability)) {
                              return availability.map((day) => (
                                <span 
                                  key={day} 
                                  className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full shadow-sm"
                                >
                                  {day}
                                </span>
                              ));
                            } else if (typeof availability === 'string') {
                              return (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full shadow-sm">
                                  {availability}
                                </span>
                              );
                            } else {
                              return (
                                <span className="text-gray-500">No availability information</span>
                              );
                            }
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter className="bg-gray-50">
                <Button 
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-sm transition-all duration-300"
                  onClick={handleAssignInstructor}
                  disabled={!selectedInstructorId || submitting || !student?.postcode || student.postcode === "Not provided"}
                >
                  {submitting ? "Assigning..." : isChangingInstructor ? "Update Instructor" : "Assign Instructor"}
                </Button>
              </CardFooter>
            </Card>
          )}
        </div>
        
        <div>
          <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
              <CardTitle className="text-xl font-semibold">Booking Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h3 className="text-sm font-medium text-gray-500">Booking ID</h3>
                  <p className="font-mono text-gray-800">{enrollment.id?.substring(0, 8) || "Unknown"}</p>
                </div>
                
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h3 className="text-sm font-medium text-gray-500">Payment Status</h3>
                  <div className="flex items-center mt-1">
                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2 shadow-sm"></div>
                    <span className="text-green-700 font-medium">Paid</span>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h3 className="text-sm font-medium text-gray-500">Payment Date</h3>
                  <p className="text-gray-800">{enrollment.paymentDate ? format(new Date(enrollment.paymentDate), "MMMM d, yyyy") : "Not available"}</p>
                </div>
                
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h3 className="text-sm font-medium text-gray-500">Booking Status</h3>
                  <div className="flex items-center mt-1">
                    {enrollment.instructorId ? (
                      <>
                        <div className="w-3 h-3 rounded-full bg-green-500 mr-2 shadow-sm"></div>
                        <span className="text-green-700 font-medium">Instructor Assigned</span>
                      </>
                    ) : (
                      <>
                        <div className="w-3 h-3 rounded-full bg-amber-500 mr-2 shadow-sm"></div>
                        <span className="text-amber-700 font-medium">Awaiting Instructor</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="border-t mt-6 pt-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-500">Course Fee</span>
                  <span className="text-gray-800">${course.price?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="flex justify-between items-center text-lg font-bold">
                  <span className="text-gray-800">Total Paid</span>
                  <span className="text-gray-900">${course.price?.toFixed(2) || "0.00"}</span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="mt-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
              <CardTitle className="text-xl font-semibold">Instructor Assignment Status</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {enrollment.instructorId ? (
                <div className="flex items-center text-green-600">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  <span className="font-medium">Instructor has been assigned</span>
                </div>
              ) : (
                <div className="flex items-center text-amber-600">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  <span className="font-medium">Instructor assignment required</span>
                </div>
              )}
              <p className="text-sm text-gray-600 mt-4">
                {enrollment.instructorId
                  ? "The instructor has been notified and will contact the student directly to confirm the booking."
                  : "Please assign an instructor to this booking so that the student can be notified about their upcoming practical lesson."}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InstructorAssignment;
